import React from "react";
import { Offcanvas } from "react-bootstrap";
import { matchPath, useLocation } from "react-router-dom";

import { useSession } from "../../contexts/SessionContext";
import { validateUserPermission } from "../../services/PermissionGuard";
import translate from "../../services/Translate";
import { ShortcutGroup, ShortcutsOffcanvasProps } from "./ShortcutsOffcanvas.d";
import { shortcutGroups } from "./ShortcutsOffcanvas.data";

const ShortcutsOffcanvas = ({ language, onHide, show }: ShortcutsOffcanvasProps) => {
  const { pathname } = useLocation();
  const { permissions } = useSession();
  const availableGroups = shortcutGroups
    .filter((group) => !group.permission || validateUserPermission(group.permission, permissions))
    .map((group) => ({
      ...group,
      shortcuts: group.shortcuts.filter(
        (shortcut) => !shortcut.permission || validateUserPermission(shortcut.permission, permissions)
      ),
    }))
    .filter((group) => group.shortcuts.length > 0);
  const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  const currentGroups = availableGroups.filter((group) =>
    group.paths.some((path) => matchPath({ path, end: true }, pathname))
  );
  const globalGroups = availableGroups.filter((group) => group.id === "GLOBAL");
  const otherGroups = availableGroups.filter((group) => group.id !== "GLOBAL" && !currentGroups.includes(group));
  const keyLabel = (key: string) => {
    if (key === "CONTROL") return isMac ? "⌘" : "Ctrl";
    return ["SHIFT", "HOLD_SHIFT"].includes(key) ? translate(language, `COMPONENTS.SHORTCUTS.KEYS.${key}`) : key;
  };

  const renderGroups = (groups: ShortcutGroup[]) =>
    groups.map((group) => (
      <section key={group.id} aria-labelledby={`shortcuts-${group.id}`} className="mb-3">
        <h3 id={`shortcuts-${group.id}`} className="s-bold mb-2">
          {translate(language, `COMPONENTS.SHORTCUTS.GROUPS.${group.id}`)}
        </h3>
        <ul className="list-unstyled mb-0">
          {group.shortcuts.map((shortcut) => (
            <li
              key={shortcut.id}
              className="d-flex justify-content-between align-items-center gap-3 py-1"
              title={translate(language, `COMPONENTS.SHORTCUTS.ITEMS.${shortcut.id}`)}
            >
              <span>{translate(language, `COMPONENTS.SHORTCUTS.LABELS.${shortcut.id}`)}</span>
              <span className="d-inline-flex align-items-center gap-1 flex-shrink-0 text-nowrap">
                {shortcut.keys.map((key, index) => (
                  <React.Fragment key={key}>
                    {index > 0 && <span className="text-body-secondary">+</span>}
                    <kbd className="bg-body-tertiary text-body border xs-regular">{keyLabel(key)}</kbd>
                  </React.Fragment>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </section>
    ));

  return (
    <Offcanvas
      id="nexus-shortcuts"
      aria-labelledby="nexus-shortcuts-title"
      show={show}
      onHide={onHide}
      placement="end"
      backdropClassName="bg-transparent"
      scroll
    >
      <Offcanvas.Header
        className="py-3 border-bottom"
        closeButton
        closeLabel={translate(language, "COMPONENTS.SHORTCUTS.CLOSE")}
      >
        <Offcanvas.Title id="nexus-shortcuts-title" className="m-bold">
          {translate(language, "COMPONENTS.SHORTCUTS.TITLE")}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="s-regular">
        <div className="mb-3">
          <h2 className="s-bold blue-font mb-3">{translate(language, "COMPONENTS.SHORTCUTS.CURRENT_PAGE")}</h2>
          {currentGroups.length ? (
            renderGroups(currentGroups)
          ) : (
            <p className="text-body-secondary mb-0">{translate(language, "COMPONENTS.SHORTCUTS.EMPTY")}</p>
          )}
        </div>
        {otherGroups.length > 0 && (
          <details key={pathname} className="border-top pt-3">
            <summary className="s-bold">{translate(language, "COMPONENTS.SHORTCUTS.OTHER_PAGES")}</summary>
            <div className="mt-3">{renderGroups(otherGroups)}</div>
          </details>
        )}
        <details className="mt-3 xs-regular text-body-secondary">
          <summary>{translate(language, "COMPONENTS.SHORTCUTS.TIPS")}</summary>
          <p className="mt-2 mb-0">{translate(language, "COMPONENTS.SHORTCUTS.NOTE")}</p>
        </details>
      </Offcanvas.Body>
      <div className="border-top px-3 pt-3 s-regular flex-shrink-0">{renderGroups(globalGroups)}</div>
    </Offcanvas>
  );
};

export default ShortcutsOffcanvas;
